// musclemap.jsx — stylised anatomical front/back muscle map (SVG).
//
//   MuscleMap({ values, view, height, highlight, secondary, onSelect, selected })
//   MuscleThumb({ primary, secondary, size })
//
// Regions are drawn for the figure's left half (viewer's left, x ≤ 100 in a
// 200-wide box) and mirrored, so both sides are always symmetric. Each region
// maps to one of the app's muscle-group names; a group can own several regions
// (e.g. Shoulders = front + side + rear delts).
//
// Fill = 5-step ramp from LL.surface3 (untrained) to LL.accent, mixed in oklab.

import React from 'react';
import { LL } from './tokens.jsx';

export const MUSCLE_GROUP_NAMES = [
  'Chest', 'Back', 'Shoulders', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Biceps',
  'Triceps', 'Core', 'Traps', 'Forearms', 'Lower Back', 'Adductors', 'Abductors', 'Neck',
];

// Body outline (left half; mirrored). Head is drawn separately.
const SILHOUETTE =
  'M100,40 L93,42 C93,52 92,58 89,63 C81,66 71,67 63,69 C51,73 44,85 43.5,100 ' +
  'C43,116 42,132 41,147 C40,153 38.5,158 37,164 C34,180 33,196 34,210 ' +
  'C33,218 30,226 31,236 C32,244 38,247 40.5,241 C42,233 42.5,222 43.5,214 ' +
  'C46,198 50,180 51.5,164 C52.5,156 54.5,150 56.5,140 C58,128 60,116 61,108 ' +
  'C62,124 64,140 66,156 C68,172 68,186 66,198 C62,210 60.5,222 61,236 ' +
  'C62,258 64,282 68,300 C69,308 68,314 68.5,320 C66,336 66,352 69.5,370 ' +
  'C71.5,382 72,392 72,398 C68,404 66,410 70,413 C78,415.5 86,414.5 90,410 ' +
  'C91,404 90,398 89,392 C90,370 92,350 91,330 C90,320 91,312 92,304 ' +
  'C94,280 96,252 97.5,232 C98,226 99,222 100,220 Z';

// [group, path] — FRONT view, left half.
const FRONT = [
  ['Neck', 'M93.5,47 C93.5,54 92.5,59 90,63 L97.5,68 C98.5,62 99,55 99,48 Z'],
  ['Traps', 'M89.5,60 C86,64.5 78,66.5 69,68.5 C77,69.5 85,69.6 91,69 C90.5,66 90,63 89.5,60 Z'],
  // deltoid split: side (outer) + front (inner)
  ['Shoulders', 'M62.5,71 C55,72 49.5,77 47,84 C44.5,91 44.5,100 46,108 C47,111.5 48,114.5 49.5,117 C50,101 54,85 62.5,71 Z'],
  ['Shoulders', 'M71,72.5 C68,71.5 65.5,71.2 64,71.3 C55.5,85 51.5,101 51.5,117.5 L54,120 C56.5,110 59.5,98 64.5,88.5 C67.5,82.5 69.5,77.5 71,72.5 Z'],
  ['Chest', 'M98.5,72.5 C90,71.5 81,72 73.5,74.5 C68.5,82 64.5,92 63.2,102 C62.6,108 63.5,112.5 66.5,116 C74.5,123.5 86.5,128 98.5,127.5 Z'],
  // lat / serratus sliver under the armpit
  ['Back', 'M64.5,120 C66.5,130 67.5,141 68,153 C70.5,147 72.5,139 74.5,129.5 C70.5,127 67.5,124 64.5,120 Z'],
  // obliques
  ['Core', 'M71.5,133 C76,137 79,142 80.5,150 C80.8,170 80.6,190 80.5,206 C77,204.5 72.5,202 68,200 C69.5,192 70.8,183 70.5,174 C71.2,160 71.3,146 71.5,133 Z'],
  // rectus abdominis (three rows + lower)
  ['Core', 'M83.5,131.5 C88.5,130.5 93.5,130.5 98.5,130.5 L98.5,148.5 C93.5,148.5 88.5,148.8 83.5,149.5 C82.8,143.5 82.8,137.5 83.5,131.5 Z'],
  ['Core', 'M83.5,152.5 C88.5,151.8 93.5,151.7 98.5,151.7 L98.5,170 C93.5,170 88.5,170.3 83.5,171 C83,165 83,158.5 83.5,152.5 Z'],
  ['Core', 'M83.6,174.2 C88.5,173.5 93.5,173.3 98.5,173.3 L98.5,213 C92,210 86.5,203.5 84.5,196.5 C83.5,189 83.4,181.5 83.6,174.2 Z'],
  // biceps
  ['Biceps', 'M57.5,113 C52.5,119 48.8,129 47.6,141 C47.2,147 48.5,151.5 51,154.5 C55,148.5 58.2,138.5 59.7,128.5 C60.4,122 59.6,116.5 57.5,113 Z'],
  // triceps (long head visible on the outer arm from the front)
  ['Triceps', 'M46.2,113 C44.3,124 43.4,135 43.2,146 C44.6,140 46,133 47.5,125.5 C47.3,121 46.8,117 46.2,113 Z'],
  // forearm flexors + brachioradialis
  ['Forearms', 'M49.5,158.5 C44,163 39.5,173 37.5,186 C36.4,195 36.4,203 37.2,210 L41.4,211.5 C44.4,199.5 48.4,185.5 51.4,172 C52.3,166.5 51.8,161.5 49.5,158.5 Z'],
  ['Forearms', 'M53.2,163 C53.2,176 50.6,191 45.8,207.5 L43.7,211 C46.3,199 49.6,186.5 52,174.5 C52.6,170.5 53,166.5 53.2,163 Z'],
  // TFL / glute medius (outer hip)
  ['Abductors', 'M64.5,206 C62.5,214 62,224 63,234 C66.2,228 68.8,220.5 70.6,212 C68.8,209 66.8,207.2 64.5,206 Z'],
  // quads: vastus lateralis, rectus femoris, vastus medialis
  ['Quads', 'M64.6,234 C63.6,256 65.2,279 69.4,297 C71,300 73.4,302 75.8,302.4 C74.2,284 73.6,262 74,241 C73.2,232 72.6,223 73,214.5 C68.8,219.5 65.6,226 64.6,234 Z'],
  ['Quads', 'M75.5,219 C80.5,213.5 86,215 88.5,222 C90.3,240 89.5,256 87,272 C85,282 83,291 80.5,299 C78.3,284 76.8,262 76.3,242 C76,234 75.6,226 75.5,219 Z'],
  ['Quads', 'M88.5,268 C91.5,276 92.4,286 91,295 C89.6,301.5 86,304.5 81.5,303.5 C85,293 87.4,281 88.5,268 Z'],
  // adductors (inner thigh)
  ['Adductors', 'M90.8,222.5 C94,224 96.8,228 97,234 C96.3,246 93.8,256 90.2,264 C91.2,250 91.4,236 90.8,222.5 Z'],
  // lower leg: tibialis / peroneals (outer) and medial gastrocnemius (inner)
  ['Calves', 'M69.6,323 C67.6,338 67.8,354 70.8,370 C72.2,378 73.2,386 74.2,393 C77,381 79,361 79.8,342 C80,334 79,327 76.6,321 C74.2,321.4 71.8,322 69.6,323 Z'],
  ['Calves', 'M85,320.5 C89.6,329 91.2,343 90,357 C89.2,366 87.2,373 84.6,378 C82,363 81.2,347 81.8,331 C82.4,327 83.4,323.4 85,320.5 Z'],
];

// [group, path] — BACK view, left half.
const BACK = [
  ['Traps', 'M99.5,43 C97,52 94,58.5 88.5,63 C82,66.5 74,68 66,70.5 C73.5,75 81,81.5 86.5,89.5 C92,98.5 96.2,112 99.5,140 Z'],
  // deltoid split: side (outer) + rear (inner)
  ['Shoulders', 'M62.5,71 C55,72 49.5,77 47,84 C44.5,91 44.5,100 46,108 C47,111.5 48,114.5 49.5,117 C50,101 54,85 62.5,71 Z'],
  ['Shoulders', 'M72,75 C69,72.5 66.5,71.6 64.3,71.6 C56,85.5 52,101 51.8,117.5 L54.2,120 C57,109 60.5,98 65.8,89.5 C68.5,84.5 70.6,79.5 72,75 Z'],
  // infraspinatus / teres / rhomboids (upper back)
  ['Back', 'M74,78 C80.5,83 85.8,90.5 89.3,99.5 C88.2,107.5 84.2,113.5 77.6,115.6 C72,112 68.4,106 67.2,98.6 C67.4,90.5 70,83.5 74,78 Z'],
  // latissimus dorsi
  ['Back', 'M65.5,105 C70,113 75.5,118.5 82.5,120 C87.5,127.5 91.2,137.5 93,148 C92.2,160 90.6,173 88,186 C81,184.5 75,181 70.6,176.5 C68.6,162 67.3,147 66.4,133 C65.8,123.5 65.5,114 65.5,105 Z'],
  // erector spinae
  ['Lower Back', 'M95,149 C97.2,156 98.5,163 98.5,171 L98.5,205 C94.5,206.5 90.6,205 89.5,201.5 C90.8,190 92.4,178 93.6,166 C94.2,160 94.7,154.5 95,149 Z'],
  // glute medius
  ['Abductors', 'M66.5,189.5 C72,189.4 79.5,190.8 87,193.8 C85,199.5 79,203.4 72.4,205 C68.6,203.4 65.8,200 64.6,196.4 C65,193.8 65.6,191.5 66.5,189.5 Z'],
  // glute max
  ['Glutes', 'M64.8,208 C71,206 80.5,205.8 89.5,206.2 C94.8,207.5 98,209.5 99,213 L99,246 C94,252.5 84,254.5 74.5,250.5 C68.5,246.5 64.5,239 62.8,229.5 C62.6,222 63.3,214.5 64.8,208 Z'],
  // hamstrings: biceps femoris (outer) + semitendinosus (inner)
  ['Hamstrings', 'M66.3,256 C66.2,272 68,287.5 72,300.5 C74,302.6 76.2,302.8 78.4,300.8 C78.2,285 78.4,269.5 80.2,256.8 C75.6,256.7 70.8,256.6 66.3,256 Z'],
  ['Hamstrings', 'M82.6,257 C82.4,272.5 82.2,288 82.4,302.6 C86.2,302.6 89,300.6 90.4,296.6 C92.2,283 93,269.5 93.2,256.8 C89.6,257 86.2,257.1 82.6,257 Z'],
  // adductor magnus (inner thigh, back)
  ['Adductors', 'M95.4,252 C97.2,263 97.3,276 95.6,288.5 C94.6,277 94.6,264 95.4,252 Z'],
  // triceps
  ['Triceps', 'M54.8,116 C49,122 45.4,132 44.2,144 C44,150 45.4,154.2 48.6,156.6 C53.4,152.5 56.8,144.5 58.8,134.5 C59.8,127.5 58.2,120.5 54.8,116 Z'],
  // forearm extensors
  ['Forearms', 'M49.5,158.5 C44,163 39.5,173 37.5,186 C36.4,195 36.4,203 37.2,210 L41.4,211.5 C44.4,199.5 48.4,185.5 51.4,172 C52.3,166.5 51.8,161.5 49.5,158.5 Z'],
  ['Forearms', 'M53.2,163 C53.2,176 50.6,191 45.8,207.5 L43.7,211 C46.3,199 49.6,186.5 52,174.5 C52.6,170.5 53,166.5 53.2,163 Z'],
  // calves: gastrocnemius lateral + medial heads, soleus
  ['Calves', 'M70.6,319 C67.6,329 66.8,341 68.4,352.5 C70,358.5 73.6,362.4 78,362.6 C79.8,350 79.9,334 78.2,320.4 C75.8,318.8 73.2,318.5 70.6,319 Z'],
  ['Calves', 'M80.6,319.6 C80.8,334.5 80.8,350 81.6,364.4 C85.6,364 88.8,358.5 90,348.5 C91,336.5 90.2,326.5 86.6,319 C84.6,318.4 82.6,318.6 80.6,319.6 Z'],
  ['Calves', 'M72.4,366 C72.4,375 73.4,384.5 74.6,391.8 C78.8,393.8 84.2,393.8 88.4,391.8 C88.6,382 89.2,372.5 89.4,363.6 C86.6,368.6 83,371 80.2,371 C77,370.8 74.4,369 72.4,366 Z'],
];

// Which side shows each group best (for thumbnails).
const FRONT_GROUPS = new Set(['Chest', 'Shoulders', 'Biceps', 'Core', 'Quads', 'Adductors', 'Forearms', 'Neck']);
const UPPER_GROUPS = new Set(['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Core', 'Traps', 'Forearms', 'Lower Back', 'Neck']);

// Group aliases so catalog/legacy spellings still land on a region.
const ALIAS = {
  lats: 'Back', 'upper back': 'Back', 'middle back': 'Back', abs: 'Core', abdominals: 'Core', obliques: 'Core',
  shoulder: 'Shoulders', delts: 'Shoulders', quadriceps: 'Quads', hamstring: 'Hamstrings', glute: 'Glutes',
  calf: 'Calves', bicep: 'Biceps', tricep: 'Triceps', forearm: 'Forearms', 'lower back': 'Lower Back',
  adductor: 'Adductors', abductor: 'Abductors', trap: 'Traps', neck: 'Neck', legs: 'Quads',
};
export function canonicalMuscle(m) {
  if (!m) return m;
  if (MUSCLE_GROUP_NAMES.includes(m)) return m;
  const k = String(m).toLowerCase();
  const hit = MUSCLE_GROUP_NAMES.find(g => g.toLowerCase() === k);
  return hit || ALIAS[k] || m;
}

// 5-step ramp: 0 → surface3; (0,.25] → 30%; (.25,.5] → 52%; (.5,.75] → 76%; >.75 → 100%.
// Ramp target: the accent on dark themes, a deeper accent on light (accentText
// equals accent on dark) so low steps stay distinguishable on white.
const RAMP_TOP = `color-mix(in oklab, ${LL.accent} 50%, ${LL.accentText})`;
export function rampColor(v) {
  if (!(v > 0)) return LL.surface3;
  const pct = v <= 0.25 ? 30 : v <= 0.5 ? 52 : v <= 0.75 ? 76 : 100;
  return pct === 100 ? RAMP_TOP : `color-mix(in oklab, ${RAMP_TOP} ${pct}%, ${LL.surface3})`;
}

function normalise({ values, highlight, secondary }) {
  const out = {};
  if (values) {
    for (const [k, v] of Object.entries(values)) {
      const c = canonicalMuscle(k);
      out[c] = Math.max(out[c] || 0, Math.max(0, Math.min(1, +v || 0)));
    }
  }
  (secondary || []).forEach(m => { const c = canonicalMuscle(m); out[c] = Math.max(out[c] || 0, 0.45); });
  (highlight || []).forEach(m => { out[canonicalMuscle(m)] = 1; });
  return out;
}

function Figure({ regions, vals, bg, base, onSelect, selected, viewBox, height, width, label, strokeW = 1.6 }) {
  const mirror = 'matrix(-1 0 0 1 200 0)';
  const renderRegions = (keyPrefix) => regions.map(([g, d], i) => {
    const sel = selected && selected === g;
    return (
      <path key={keyPrefix + i} d={d}
        onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(g); } : undefined}
        style={{
          fill: rampColor(vals[g]),
          stroke: sel ? LL.fg : bg, strokeWidth: sel ? strokeW * 1.1 : strokeW, strokeLinejoin: 'round',
          cursor: onSelect ? 'pointer' : 'default',
          transition: 'fill 240ms cubic-bezier(.2,.8,.2,1)',
        }}>
        {onSelect && <title>{g}</title>}
      </path>
    );
  });
  return (
    <svg viewBox={viewBox} height={height} width={width} style={{ display: 'block', overflow: 'visible' }}
      role="img" aria-label={label}>
      {/* silhouette */}
      <g style={{ fill: base }}>
        <ellipse cx="100" cy="24" rx="15.5" ry="19" />
        <path d={SILHOUETTE} />
        <path d={SILHOUETTE} transform={mirror} />
      </g>
      <g>{renderRegions('l')}</g>
      <g transform={mirror}>{renderRegions('r')}</g>
    </svg>
  );
}

/**
 * MuscleMap({ values?: {[group]: 0..1}, view='both'|'front'|'back', height=220,
 *             highlight?: string[], secondary?: string[], onSelect?(group),
 *             selected?: string, bg?: string (card colour behind the map), labels=true })
 */
export function MuscleMap({
  values, view = 'both', height = 220, highlight, secondary, onSelect, selected,
  bg = LL.surface, labels = true,
}) {
  const vals = React.useMemo(() => normalise({ values, highlight, secondary }), [values, highlight, secondary]);
  const vb = '26 2 148 416';
  const w = height * 148 / 416;
  const base = `color-mix(in oklab, ${LL.surface3} 55%, ${bg})`;
  const fig = (regions, label) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <Figure regions={regions} vals={vals} bg={bg} base={base} onSelect={onSelect} selected={selected}
        viewBox={vb} height={height} width={w} label={`${label} muscle map`} />
      {labels && (
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, color: LL.fgDim, letterSpacing: 1.2, textTransform: 'uppercase' }}>{label}</div>
      )}
    </div>
  );
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: Math.round(height * 0.12) }}>
      {view !== 'back' && fig(FRONT, 'Front')}
      {view !== 'front' && fig(BACK, 'Back')}
    </div>
  );
}

/**
 * MuscleThumb({ primary: string[], secondary: string[], size=44, bg? })
 * Tiny tile for list rows: picks front/back by where the primary muscles show
 * and crops to the upper or lower body.
 */
export function MuscleThumb({ primary = [], secondary = [], size = 44, bg = LL.surface2, radius = LL.r.sm }) {
  const prim = (primary || []).map(canonicalMuscle).filter(Boolean);
  const sec = (secondary || []).map(canonicalMuscle).filter(Boolean);
  const vals = React.useMemo(() => normalise({ highlight: prim, secondary: sec }), [prim.join(), sec.join()]);
  const focus = prim.length ? prim : sec;
  const frontScore = focus.filter(m => FRONT_GROUPS.has(m)).length;
  const isFront = focus.length === 0 ? true : frontScore >= focus.length / 2;
  const upperScore = focus.filter(m => UPPER_GROUPS.has(m)).length;
  const isUpper = focus.length === 0 ? true : upperScore >= focus.length / 2;
  // square crops of the 200×420 figure
  const calvesOnly = focus.length > 0 && focus.every(m => m === 'Calves');
  const viewBox = isUpper ? '24 8 152 152' : calvesOnly ? '36 280 128 128' : '30 196 140 140';
  // Body and untrained muscles are tinted from the theme's faint foreground so
  // the figure reads on both the dark and the light tile (surface3 alone all
  // but vanishes on the light theme's near-white tile).
  const base = `color-mix(in oklab, ${LL.fgFaint} 32%, ${bg})`;
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, background: bg, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      '--ll-surface3': `color-mix(in oklab, ${LL.fgFaint} 52%, ${bg})`,
    }} aria-hidden="true">
      <Figure regions={isFront ? FRONT : BACK} vals={vals} bg={bg} base={base}
        viewBox={viewBox} height={size * 0.92} width={size * 0.92} label="" strokeW={2.2} />
    </div>
  );
}
